import { layerEntriesOf, layerListEntryUuid, layerOf } from '../../shared/array-types';
import { readItemField } from '../compile/item-field-reader';
import { lookupRandomTableRange, randomTableMaxEnd } from '../rolls/random-table';
import { mathRandomDie, type DieSource } from '../rolls/dice-roll';

type Row = Record<string, unknown>;
export interface NameTables {
    first: readonly string[];
    last: string;
    domains?: readonly string[];
    symbol?: string;
}
const MALE = '0D629F19-23AC-18BE-B79C-73CB52703F66';
const FEMALE = '1214C099-5021-24B5-5D8D-608B832CE70A';
const HUMAN_MALE = 'F145078C-472C-6095-8799-923202A8BADC';
const HUMAN_FEMALE = '1919E41C-FB3B-C2FF-E43D-02DD252CF95F';

/** Subtype tables follow template placeholders; the last nonempty native reference wins. */
function lastTable(caster: unknown, key: string): string {
    let chosen = '';
    for (const entry of layerEntriesOf(caster, key)) {
        const value = typeof entry === 'string' ? entry.trim() : layerListEntryUuid(entry);
        if (value) chosen = value;
    }
    return chosen;
}

export function creatureNameTables(caster: unknown, gender: string): { first: string[]; last: string } {
    const male = lastTable(caster, 'random_name_table_male'), female = lastTable(caster, 'random_name_table_female');
    const generic = lastTable(caster, 'random_name_table');
    const first = generic && !male && !female ? [generic] : gender.toUpperCase() === MALE ? [male || HUMAN_MALE]
        : gender.toUpperCase() === FEMALE ? [female || HUMAN_FEMALE] : [male || HUMAN_MALE, female || HUMAN_FEMALE];
    return { first, last: lastTable(caster, 'random_name_table_last_name') };
}

function clean(part: string): string {
    return part.replace(/[\u200B\u200C\u200D\uFEFF]/g, '').replace(/[\u00A0\u2009\u202F]/g, ' ').replace(/\s+/gu, ' ').trim();
}

/** The name helper rolls one max-range die per table; authored gameplay pools/chains are unrelated. */
export function generateCreatureName(tables: NameTables, savedRanges: readonly Row[], draw: DieSource = mathRandomDie): string {
    const field = (row: Row, key: string): unknown => readItemField(row, key) ?? layerOf(row, key);
    const roll = (id: string): string => {
        const ranges = savedRanges.filter(row => String(readItemField(row, 'item_id') ?? '').toUpperCase() === id.toUpperCase())
            .map(row => ({ begin: Number(field(row, 'roll_range_begin') ?? 0), end: Number(field(row, 'roll_range_end') ?? 0),
                effect: String(field(row, 'roll_result_effect') ?? '') }));
        const die = randomTableMaxEnd(ranges);
        if (die < 1) throw new Error('The name table has no ranges.');
        const range = lookupRandomTableRange(ranges, draw(die));
        if (!range) throw new Error('The name table has no result for this roll.');
        return clean(range.effect ?? '').replace(/^[a-z]/, letter => letter.toUpperCase());
    };
    const choose = (ids: readonly string[]): string => ids.length === 1 ? ids[0]! : ids[draw(ids.length) - 1]!;
    const name = roll(choose(tables.first));
    const last = tables.last ? roll(tables.last) : '';
    if (tables.domains && tables.symbol) {
        return `${name}${last.replace(/^[A-Z]/, letter => letter.toLowerCase())} (${roll(choose(tables.domains))}) [${roll(tables.symbol)}]`;
    }
    return clean(last ? `${name} ${last}` : name);
}

/** The ordinary tool requests ten distinct results; a broken/undersized table must not hang the page. */
export function generateCreatureNames(tables: NameTables, savedRanges: readonly Row[], count = 10, draw: DieSource = mathRandomDie): string[] {
    const names = new Set<string>();
    for (let attempts = 0; names.size < count && attempts < count * 1000; attempts++) {
        names.add(generateCreatureName(tables, savedRanges, draw));
    }
    if (names.size < count) throw new Error('The name tables could not supply enough distinct names.');
    return [...names];
}
